import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { Baseurl } from "../../config/BaseUrl";
import {
  setPropertybySubcatStatus,
  setPropertyfilterbySubcat,
} from "../../redux/property/PropertySlice";

import "./BHKchoiceInMindNew.css";

/* ================= Vector SVG Icons matching Reference ================= */

/* Small House Brand Icon for Eyebrow */
const SmallHouseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M3 10L12 3l9 7v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V10z" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <rect x="9" y="12" width="2.5" height="2.5" rx="0.4" fill="#ea580c" />
    <rect x="12.5" y="12" width="2.5" height="2.5" rx="0.4" fill="#64748b" />
    <rect x="9" y="15.5" width="2.5" height="2.5" rx="0.4" fill="#64748b" />
    <rect x="12.5" y="15.5" width="2.5" height="2.5" rx="0.4" fill="#ea580c" />
  </svg>
);

/* Bed Icon matching reference card badges */
const BedIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7v13" />
    <path d="M21 7v13" />
    <path d="M3 15h18" />
    <path d="M3 11h18a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2H3" />
    <rect x="6" y="8" width="5" height="3" rx="1" fill="currentColor" opacity="0.3" />
    <rect x="13" y="8" width="5" height="3" rx="1" fill="currentColor" opacity="0.3" />
  </svg>
);

/* Arrow Right Button Icon */
const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const BHKchoiceInMind = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const scrollRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);

  const supcat = "Residential";
  const cat = "Buy";
  const subcat1 = "All";
  const city = "Hyderabad";

  const [bhkCounts, setBhkCounts] = useState({});

  useEffect(() => {
    const fetchBhkData = async () => {
      try {
        const url = `${Baseurl}/api/v1/property/allbhk`;
        const res = await axios.get(url);
        if (res.data?.success && Array.isArray(res.data.bhk)) {
          const countMap = {};
          res.data.bhk.forEach((item) => {
            const key = String(item._id).trim();
            countMap[key] = item.count;
          });
          setBhkCounts(countMap);
        }
      } catch (error) {
        // Fallbacks rendered seamlessly
      }
    };

    fetchBhkData();
  }, []);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, offsetWidth } = scrollRef.current;
      const index = Math.round(scrollLeft / (offsetWidth * 0.65));
      setActiveSlide(Math.min(Math.max(index, 0), 3));
    }
  };

  const scrollToSlide = (index) => {
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.querySelector(".mgh-bhk-card")?.offsetWidth || 240;
      scrollRef.current.scrollTo({
        left: index * (cardWidth + 14),
        behavior: "smooth",
      });
      setActiveSlide(index);
    }
  };

  const handleBhkClick = (bhkKey) => {
    navigate(`/property-search/${supcat}/${cat}/${subcat1}/${city}`);
  };

  const bhkCards = [
    {
      id: "1-bhk",
      bhkKey: "1",
      title: "1 BHK",
      count: `${bhkCounts["1"] || 103}+ Properties`,
      desc: "Compact & cozy flats, studio apartments & budget homes.",
      image: "/assets/images/bhk/bhk_1.jpg",
      fallbackImage: "/assets/images/sliderImg/d_hp_availability_2.webp",
      themeClass: "mgh-bhk-card-rose",
      badge: "Compact Living",
    },
    {
      id: "2-bhk",
      bhkKey: "2",
      title: "2 BHK",
      count: `${bhkCounts["2"] || 133}+ Properties`,
      desc: "Most popular choice for small families & modern lifestyle.",
      image: "/assets/images/bhk/bhk_2.jpg",
      fallbackImage: "/assets/images/sliderImg/d_hp_availability_3.webp",
      themeClass: "mgh-bhk-card-blue",
      badge: "Popular Choice",
    },
    {
      id: "3-bhk",
      bhkKey: "3",
      title: "3 BHK",
      count: `${bhkCounts["3"] || 35}+ Properties`,
      desc: "Spacious premium apartments & homes with extra comfort.",
      image: "/assets/images/bhk/bhk_3.jpg",
      fallbackImage: "/assets/images/sliderImg/d_hp_property_type_1.webp",
      themeClass: "mgh-bhk-card-green",
      badge: "Spacious Family",
    },
    {
      id: "4-bhk",
      bhkKey: "4",
      title: "4+ BHK",
      count: `${bhkCounts["4"] || bhkCounts["4+"] || 25}+ Properties`,
      desc: "Luxury penthouses, grand villas & large duplex living.",
      image: "/assets/images/bhk/bhk_4.jpg",
      fallbackImage: "/assets/images/sliderImg/d_hp_property_type_2.webp",
      themeClass: "mgh-bhk-card-amber",
      badge: "Luxury & Grand",
    },
  ];

  return (
    <section className="mgh-bhk-section" aria-label="Browse by BHK Choice">
      {/* Header Area */}
      <div className="mgh-bhk-header">
        <div className="mgh-bhk-header-left">
          <div className="mgh-bhk-eyebrow-wrap">
            <div className="mgh-bhk-header-icon" aria-hidden="true">
              <SmallHouseIcon />
            </div>
            <div className="mgh-bhk-eyebrow">
              <span>FIND YOUR PERFECT HOME</span>
              <span className="mgh-bhk-eyebrow-line"></span>
            </div>
          </div>
          <h2 className="mgh-bhk-title">
            BHK choice <span className="mgh-bhk-highlight">in mind?</span>
          </h2>
         
        </div>

        {/* Decorative Skyline Blueprint & Building Graphic */}
        <div className="mgh-bhk-header-right" aria-hidden="true">
          <div className="mgh-bhk-decor-blueprint"></div>
          <img
            src="/assets/images/possession/possession_new_launch.jpg"
            alt=""
            className="mgh-bhk-decor-building"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>
      </div>

      {/* 4 BHK Cards Grid / Mobile Swipe Carousel */}
      <div
        className="mgh-bhk-grid"
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {bhkCards.map((card) => (
          <div
            key={card.id}
            className={`mgh-bhk-card ${card.themeClass}`}
            onClick={() => handleBhkClick(card.bhkKey)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleBhkClick(card.bhkKey);
              }
            }}
          >
            {/* Card Top Info */}
            <div className="mgh-bhk-card-top">
              <div className="mgh-bhk-card-head-row">
                <div className="mgh-bhk-icon-box" aria-hidden="true">
                  <BedIcon />
                </div>
                <div className="mgh-bhk-arrow-btn" aria-label={`View ${card.title} Properties`}>
                  <ArrowRightIcon />
                </div>
              </div>

              <h3 className="mgh-bhk-card-title">{card.title}</h3>
              <div className="mgh-bhk-card-count">{card.count}</div>
              <p className="mgh-bhk-card-desc">{card.desc}</p>
            </div>

            {/* Framed Large High-Quality Photo Viewport */}
            <div className="mgh-bhk-card-img-wrap">
              <img
                src={card.image}
                alt={`${card.title} interior preview`}
                className="mgh-bhk-card-img"
                loading="lazy"
                onError={(e) => {
                  e.target.src = card.fallbackImage;
                }}
              />
              <span className="mgh-bhk-img-tag">{card.badge}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile Pagination Indicator Dots */}
      <div className="mgh-bhk-mobile-dots" aria-hidden="true">
        {bhkCards.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`mgh-bhk-dot ${activeSlide === i ? "active" : ""}`}
            onClick={() => scrollToSlide(i)}
            aria-label={`Go to ${bhkCards[i].title}`}
          />
        ))}
      </div>
    </section>
  );
};

export default BHKchoiceInMind;
